import { inject, Injector } from "@angular/core";
import { ActivatedRouteSnapshot, DetachedRouteHandle, destroyDetachedRouteHandle, Route, RouteReuseStrategy, Router } from "@angular/router";

type NavigationState = Record<string, unknown>;
type ReuseCondition = boolean | ((previousUrl: string, state: NavigationState) => boolean);

/**
 * Custom route reuse strategy that allows routes to specify reuse behavior via route data.
 *
 * Routes can configure reuse using the `data.reuse` property with either:
 * - A boolean value for unconditional reuse
 * - A function that receives the previous URL and returns a boolean for conditional reuse
 *
 * @example
 * // Always reuse the component
 * {
 *   path: "dashboard",
 *   component: DashboardComponent,
 *   data: { reuse: true }
 * }
 *
 * @example
 * // Conditionally reuse based on previous URL
 * {
 *   path: "organizations",
 *   component: OrganizationsComponent,
 *   data: {
 *     reuse: (previousUrl: string) => previousUrl.startsWith("/admin/organizations/")
 *   }
 * }
 */
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
    private readonly injector = inject(Injector);
    private handlers = new Map<Route | null, DetachedRouteHandle>();
    private previousUrl = "";

    public shouldDetach(route: ActivatedRouteSnapshot): boolean {
        const reuseValue = route.data["reuse"];
        return reuseValue !== undefined;
    }

    public store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
        if (!this.shouldDetach(route)) return;

        if (handle) this.handlers.set(route.routeConfig, handle);
        else this.handlers.delete(route.routeConfig);
    }

    public shouldAttach(route: ActivatedRouteSnapshot): boolean {
        const handle = this.handlers.get(route.routeConfig);
        if (!handle) return false;
        if (this.shouldReuseComponent(route)) return true;

        this.handlers.delete(route.routeConfig);
        destroyDetachedRouteHandle(handle);

        return false;
    }

    public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        return this.shouldReuseComponent(route) ? (this.handlers.get(route.routeConfig) ?? null) : null;
    }

    public shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        this.previousUrl = this.getFullPath(curr);
        return future.routeConfig === curr.routeConfig;
    }

    private shouldReuseComponent(route: ActivatedRouteSnapshot): boolean {
        const reuseValue = route.data["reuse"] as ReuseCondition | undefined;

        if (typeof reuseValue === "function") {
            return reuseValue(this.previousUrl, this.getNavigationState());
        }

        return reuseValue === true;
    }

    private getNavigationState(): NavigationState {
        return this.injector.get(Router).getCurrentNavigation()?.extras.state ?? {};
    }

    private getFullPath(route: ActivatedRouteSnapshot): string {
        const segments: string[] = [];
        let current: ActivatedRouteSnapshot | null = route;

        while (current) {
            if (current.url.length > 0) {
                segments.unshift(...current.url.map((segment) => segment.path));
            }
            current = current.parent;
        }

        return "/" + segments.join("/");
    }
}
